// AgreementsRegisterView.tsx
import React from 'react';
import styled from 'styled-components';
import { BackButton } from '@/presentation/modules/procedures/components';
import { Block, Heading } from '@/presentation/components';
import ContainerProcedure from '@/presentation/modules/procedures/components/ContainerProcedure';
import Spinner from '@/presentation/components/Spinner';
import Step from '../../../requests/UpdatePhoto/Register/components/Step';
import { TextTitle } from '../../../certificates-constancies/type1/register/components';
import FileUpload from '@/presentation/reusables/Upload';
import { SaveButton } from '../../bse/components/styled';
import AgreementsInfoCard from '../components/AgreementsInfoCard';
import ModalConfirmation from '@/presentation/modules/procedures/components/Modal/Modal';
import imgWarning from '../../../../../../../assets/illustrations/warning.svg';
import MyModal from '@/presentation/modules/procedures/components/Modals/MyModal';
import { ElementClaim } from '../../../requests/EmployabilityCharter/register/EmployabilityCharterRegisterViewController';
import ConfirmationQuit from '../../../requests/UpdatePhoto/Register/components/ConfirmationQuit';
import DateHelpers from '@/core/helpers/DateHelper';
import Processing from '@/presentation/components/Processing';

export interface AgreementsRegisterViewProps {
  beneficiaryName?: string;
  documentsList: string[];
  requirementsAuto: { text: string; valid: boolean }[];
  isLoading: boolean;
  isProcessing?: boolean;
  procedureSelectedInfo: {
    steps: { name: string; order: number }[];
    name: string;
    amount: string;
    duration: string;
    endDate: string;
    isLoading: boolean;
  };
  base64: any[];
  maxFiles: number;
  maxSizeMB: number;
  emitFiles: (files: File[]) => void;
  onSubmit: () => void;
  handleBack: () => void;
  handleQuitModalModified: () => void;
  handleCancelModalModified: () => void;
  showModalModified?: boolean;
  redirectToTicket: () => void;
  showModalError: boolean;
  handleCloseModalError: () => void;
  handleClickModalError: () => void;
  validateDocuments: boolean;
  setValidateDocuments: (value: boolean) => void;
}

const InfoText = styled.p`
  font-family: Lato;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
`;

const SeparatorLine = styled.div.attrs((props) => ({
  color: props.color
}))`
  min-height: 1px;
  background-color: ${(props) => props.color};
  flex: 1;
  width: 100%;
`;
const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  @media (min-width: 768px) {
    width: 639px;
    margin: 0 auto;
  }
`;
const AgreementsRegisterView: React.FC<AgreementsRegisterViewProps> = ({
  beneficiaryName,
  documentsList,
  requirementsAuto,
  isLoading,
  procedureSelectedInfo,
  base64,
  maxFiles,
  maxSizeMB,
  isProcessing,
  emitFiles,
  onSubmit,
  handleBack,
  handleQuitModalModified,
  handleCancelModalModified,
  showModalModified = false,
  redirectToTicket,
  showModalError,
  handleCloseModalError,
  handleClickModalError,
  validateDocuments,
  setValidateDocuments
}) => (
  <ContainerProcedure>
    <SubContainer style={{ marginBottom: 24 }}>
      <Block
        customized
        width="100%"
        display="flex"
        justifyContent="flex-start"
        padding="16px 0 0 0"
      >
        <BackButton onClick={handleBack} fontSize="14px" />
      </Block>
    </SubContainer>
    {isLoading ? (
      <Spinner />
    ) : (
      <SubContainer>
        <AgreementsInfoCard
          name={procedureSelectedInfo?.name}
          amount={Number(procedureSelectedInfo?.amount)}
          duration={procedureSelectedInfo?.duration}
          date={
            DateHelpers.formatDateString(procedureSelectedInfo?.endDate) ?? ''
          }
          requirementsAuto={requirementsAuto}
          documentsList={documentsList}
        />
        <TextTitle title="Progreso" />
        <Block customized padding="12px 0px 0px 0px">
          <Step
            name={procedureSelectedInfo?.steps[0]?.name}
            order={procedureSelectedInfo?.steps[0]?.order}
            isCurrent
          >
            <Block customized backgroundColor="#fff" borderRadius="8px">
              <Block customized padding="16px 20px">
                <Heading
                  level={3}
                  text="Tipo de beneficiario:"
                  size={14}
                  fontWeight="700"
                  lineHeight={20}
                  align="flex-start"
                  color="#161D1F"
                />
                <InfoText>{beneficiaryName}</InfoText>
              </Block>
              <Block customized padding="0px 20px 16px 20px">
                <Heading
                  text="Documentos de sustento"
                  level={3}
                  size={14}
                  lineHeight={20}
                  fontWeight="700"
                  align="flex-start"
                  width="100%"
                  color="#161D1F"
                />
                <Block customized padding="8px 16px">
                  <ul style={{ listStyle: 'disc' }}>
                    {documentsList.map((doc) => (
                      <li key={doc}>{doc}</li>
                    ))}
                  </ul>
                </Block>
              </Block>
              <SeparatorLine color="#ECF4F9" />
              <Block customized padding="16px 20px 0px 20px">
                <Heading
                  text="Adjunta tus documentos"
                  level={3}
                  size={14}
                  color="var(--text-text02)"
                  lineHeight={20}
                  fontWeight="700"
                  align="flex-start"
                  width="100%"
                />
                <FileUpload
                  acceptedFiles={['jpg', 'pdf', 'png']}
                  dataFiles={base64 || []}
                  deleteFiles={() => {}}
                  downloadFiles={() => {}}
                  emitFiles={emitFiles}
                  idValue="id"
                  maxFiles={maxFiles}
                  maxSizeMegabytes={maxSizeMB}
                  nameValue="originalName"
                  pathValue="path"
                  sizeValue="size"
                  customText
                  validateDocuments={setValidateDocuments}
                />
              </Block>
              <Block customized padding="16px 20px">
                <SaveButton
                  data-testid="cmp-button-save-agreements"
                  onClick={onSubmit}
                  disabled={base64.length === 0 || !validateDocuments}
                >
                  Enviar solicitud
                </SaveButton>
              </Block>
            </Block>
          </Step>
          <Step
            name={procedureSelectedInfo?.steps[1]?.name}
            order={procedureSelectedInfo?.steps[1]?.order}
            isLast
            disabled
          />
        </Block>
        <Processing isOpen={isProcessing} />
        <ModalConfirmation
          show={showModalModified}
          handleClose={handleCancelModalModified}
        >
          <ConfirmationQuit
            handleQuit={handleQuitModalModified}
            handleCancel={handleCancelModalModified}
          />
        </ModalConfirmation>
        <MyModal
          img={imgWarning}
          data={{
            description: (
              <>Parece que hubo un error y no pudimos generar tu solicitud.</>
            ),
            description2: (
              <>
                Si el problema continúa, escríbenos a{' '}
                <ElementClaim onClick={redirectToTicket}>
                  registro de quejas y reclamos.
                </ElementClaim>
              </>
            ),
            title: '¡Oh no!'
          }}
          handlerClick={handleClickModalError}
          handlerClose={handleCloseModalError}
          show={showModalError}
        />
      </SubContainer>
    )}
  </ContainerProcedure>
);

export default AgreementsRegisterView;
